<template lang="pug">
.collections-block(v-if="collectionsBlock.groups.length > 0")
  // Section title
  .section-title(v-if="collectionsBlock.title")
    h6.mb-0 {{ collectionsBlock.title.toUpperCase() }}

  // Horizontal scrollable collections container
  .collections-scroll-wrapper
    .collections-scroll-container
      .collection-item(
        v-for="(group, index) in collectionsBlock.groups"
        :key="index"
        @click="handleCollectionClick(group, index)"
      )
        // Collection cover image
        .collection-cover-wrapper
          img(
            v-if="group.coverImageUrl"
            :src="group.coverImageUrl"
            :alt="group.title"
            @error="handleImageError"
          ).collection-cover
          .collection-cover-placeholder(v-else)

        // Collection title
        .collection-title
          span {{ group.title }}
</template>

<script lang="ts">
import {
  defineComponent, computed, inject, PropType, Ref,
} from 'vue';
import { useRouter } from 'vue-router';
import { CollectionsBlock as CollectionsBlockType } from '../types';

export default defineComponent({
  name: 'CollectionsBlock',
  props: {
    block: {
      type: Object as PropType<CollectionsBlockType>,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();

    // Computed
    const collectionsBlock = computed(() => props.block as CollectionsBlockType);

    // Methods
    const pageSlug = inject<Ref<string> | null>('staticPageSlug', null);

    const handleCollectionClick = (group: any, index: number) => {
      // Store collection data in sessionStorage for the detail page
      // This allows the page to persist on refresh and handles navigation properly
      try {
        sessionStorage.setItem('collectionGroup', JSON.stringify(group));
      } catch (e) {
        console.error('Failed to store collection data:', e);
      }

      // The URL identifies the collection, so the page can be reloaded and shared
      router.push({
        path: '/web/collection',
        query: {
          page: pageSlug?.value || 'home-page',
          block: collectionsBlock.value.id,
          group: String(index),
        },
      });
    };

    const handleImageError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      target.style.display = 'none';
      console.error('Failed to load collection cover image:', target.src);
    };

    return {
      collectionsBlock,
      handleCollectionClick,
      handleImageError,
    };
  },
});
</script>

<style lang="scss" scoped>
.collections-block {
  margin-bottom: 48px;
  padding: 0 24px;

  @media (min-width: 768px) {
    padding: 0 40px;
    margin-bottom: 56px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 64px;
    margin-bottom: 64px;
  }
}

.section-title {
  margin-bottom: 24px;

  h6 {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #1a1a1a;
    margin: 0;
    
    @media (min-width: 768px) {
      font-size: 15px;
    }
  }
}

.collections-scroll-wrapper {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.collections-scroll-container {
  display: flex;
  gap: 16px;
  padding-bottom: 8px;

  @media (min-width: 768px) {
    gap: 20px;
  }
  
  @media (min-width: 1024px) {
    gap: 24px;
  }
}

.collection-item {
  flex-shrink: 0;
  width: 120px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-8px);
    
    .collection-cover-wrapper {
      box-shadow: 0 8px 24px rgba(13, 148, 136, 0.15);
    }
    
    .collection-title span {
      color: #0d9488;
    }
  }

  @media (min-width: 768px) {
    width: 144px;
  }
}

.collection-cover-wrapper {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  border: 1px solid #e5e7eb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 768px) {
    width: 144px;
    height: 144px;
  }
}

.collection-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  .collection-item:hover & {
    transform: scale(1.05);
  }
}

.collection-cover-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
}

.collection-title {
  padding: 0 4px;
  
  span {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    color: #1a1a1a;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.2s;
    
    @media (min-width: 768px) {
      font-size: 14px;
    }
  }
}
</style>
